import { Toaster } from "@super-resume-new/ui/components/sonner";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { PostHogProvider, usePostHog } from "@posthog/react/slim";
// The slim build, not the default one. See PostHog() below.
import slimPostHog from "posthog-js/dist/module.slim.js";
import type { PostHog as PostHogInstance } from "posthog-js";

// One class at runtime, two unrelated declarations to tsc: the slim entry ships
// its own .d.ts in which every extension is optional, and @posthog/react types
// its `client` against the default entry's copy.
const posthogClient = slimPostHog as unknown as PostHogInstance;
import { ThemeProvider } from "next-themes";
import { useEffect, useRef, type ReactNode } from "react";

import { authClient } from "@/lib/auth-client";
import type { orpc } from "@/utils/orpc";

import Header from "../components/header";
import { Footer } from "../components/footer";
import { CookieConsent } from "../components/cookie-consent";
import { AuthModalProvider } from "@/context/auth-modal";
import { jsonLdScript, seo } from "@/utils/seo";
import { siteGraph } from "@/utils/schema";

import appCss from "../index.css?url";
export interface RouterAppContext {
  orpc: typeof orpc;
  queryClient: QueryClient;
}

const ROOT_TITLE = "JobCues: Curated Jobs & ATS Resume Rewriter";
const ROOT_DESCRIPTION = "JobCues provides curated feed of verified jobs with a free ATS checker, 1-Click Resume Tailor & ATS Booster powered by your Master Career Profile with an auto application tracker.";

export const Route = createRootRouteWithContext<RouterAppContext>()({
  head: () => {
    // Defaults only. No `path` on purpose: TanStack dedupes `meta` by name but
    // appends `links`, so a canonical here would ship a second <link rel=
    // "canonical"> on every page. Each route sets its own.
    const { meta, links } = seo({
      title: ROOT_TITLE,
      description: ROOT_DESCRIPTION,
    });

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        ...meta,
        { name: "theme-color", content: "#ffffff", media: "(prefers-color-scheme: light)" },
        { name: "theme-color", content: "#0a0a0a", media: "(prefers-color-scheme: dark)" },
        { name: "apple-mobile-web-app-title", content: "JobCues" },
        { name: "application-name", content: "JobCues" },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
        { rel: "icon", href: "/icon.svg", type: "image/svg+xml" },
        { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
        { rel: "manifest", href: "/site.webmanifest" },
        { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
        // GA is third-party and render-blocking on a cold connection.
        { rel: "preconnect", href: "https://www.googletagmanager.com" },
        { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
        ...links,
      ],
      scripts: [
        // Organization + WebSite. Server-rendered, because AI crawlers do not
        // run JavaScript and this is what resolves "JobCues" as an entity.
        jsonLdScript(siteGraph()),
        {
          src: "https://www.googletagmanager.com/gtag/js?id=G-QHJJ6D6L42",
          async: true,
        },
        {
          children: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', {
  'analytics_storage': 'denied',
  'ad_storage': 'denied',
  'ad_user_data': 'denied',
  'ad_personalization': 'denied'
});
gtag('js', new Date());
gtag('config', 'G-QHJJ6D6L42');`,
        },
      ],
    };
  },

  component: RootDocument,
});

/**
 * PostHog, on the slim bundle.
 *
 * `posthog-js`'s default entry is one pre-bundled 273 kB file that registers
 * every extension — session replay, surveys, product tours, the toolbar — up
 * front, and it was 29% of the entry chunk that blocks first render. The slim
 * entry (133 kB) carries the core — capture, autocapture, identify, feature
 * flags — and fetches an extension from the PostHog asset host the first time
 * something needs it, which is already how the default entry loads the session
 * recorder. VITE_PUBLIC_POSTHOG_HOST must therefore be a host that serves
 * /static/*.js; PostHog Cloud is, a reverse proxy that only forwards /e/ is not.
 *
 * The cost of the slim entry is that `@posthog/react`'s apiKey prop is gone —
 * it lives in the default entry and would drag it back in — so init happens
 * here. It happens in an effect for two reasons: this component renders during
 * SSR, where there is no document to instrument, and it is what the upstream
 * provider does, so the ordering against consumers' own effects is unchanged.
 */
let initialized = false;

function PostHog({ children }: { children: ReactNode }) {
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
  const apiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

  useEffect(() => {
    // A module-level flag, not a ref: two mounts of this component are still
    // one PostHog, and StrictMode runs every effect twice in development.
    if (initialized || !apiKey || !apiHost) return;
    initialized = true;

    posthogClient.init(apiKey, {
      api_host: apiHost,
      defaults: "2025-05-24",
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      debug: import.meta.env.DEV,
    });
  }, [apiKey, apiHost]);

  if (!apiKey || !apiHost) {
    if (import.meta.env.DEV) {
      const missingVariable = apiKey
        ? "VITE_PUBLIC_POSTHOG_HOST"
        : "VITE_PUBLIC_POSTHOG_PROJECT_TOKEN";
      throw new Error(
        `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
      );
    }
  }

  // The provider goes up even with no key, so usePostHog() is never undefined.
  // Every consumer calls posthog.capture() unguarded; dropping the provider
  // turned a missing env var into a TypeError on click. posthog-js ignores
  // calls made before init(), so an un-inited client is the safe no-op here.
  return <PostHogProvider client={posthogClient}>{children}</PostHogProvider>;
}

function PostHogIdentity() {
  const posthog = usePostHog();
  const { data: session, isPending } = authClient.useSession();
  const identifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (isPending) return;

    const user = session?.user;
    if (!user) {
      identifiedUserId.current = null;
      return;
    }

    if (identifiedUserId.current === user.id) return;

    if (identifiedUserId.current) {
      posthog.reset();
    }

    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
    });
    identifiedUserId.current = user.id;
  }, [isPending, posthog, session?.user]);

  return null;
}

function RootDocument() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <PostHog>
          <PostHogIdentity />
          {/* @ts-expect-error - next-themes React 19 type definition */}
          <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
            <AuthModalProvider>
              <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:left-0 focus:top-0 focus:z-50 focus:rounded-br-md focus:bg-primary focus:px-4 focus:py-2 focus:font-semibold focus:text-primary-foreground"
              >
                Skip to main content
              </a>
              {isAdminRoute ? (
                <div className="h-svh w-full overflow-hidden bg-background">
                  <Outlet />
                </div>
              ) : (
                <div className="flex min-h-svh flex-col">
                  <Header />
                  <div className="flex-1">
                    <Outlet />
                  </div>
                  <Footer />
                </div>
              )}
              <Toaster richColors />
              <CookieConsent />
              <TanStackRouterDevtools position="bottom-left" />
              <ReactQueryDevtools position="bottom" buttonPosition="bottom-right" />
            </AuthModalProvider>
          </ThemeProvider>
        </PostHog>
        <Scripts />
      </body>
    </html>
  );
}
